import * as TsLog from 'tslog';

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Abstract logger class
 *
 * Defines a small API surface, used to implement mocks or facades for other libraries.
 */
export abstract class AbstractLogger {
  abstract trace(message: string, payload?: object): void;

  abstract debug(message: string, payload?: object): void;

  abstract info(message: string, payload?: object): void;

  abstract warn(message: string, payload?: object): void;

  abstract error(message: string, payload?: object): void;

  abstract child(name: string): AbstractLogger;

  abstract setLogLevel(level: LogLevel): void;
}

/**
 * Production-ready logger implementation based on tslog.
 *
 * Note: Console in this context is meant as the browser's console, not Aura
 * console.
 */
export class ConsoleLogger implements AbstractLogger {
  private instance: TsLog.Logger<TsLog.ILogObj>;

  constructor(name: string, parentLogger?: TsLog.Logger<TsLog.ILogObj>, defaultLogLevel: LogLevel = 'error') {
    if (parentLogger !== undefined) {
      this.instance = parentLogger.getSubLogger({ name });
    } else {
      this.instance = new TsLog.Logger({ name });
    }

    this.setLogLevel(defaultLogLevel);
    this.debug('Logger is created');
  }

  trace(message: string, payload?: object): void {
    this.instance.trace(message, payload ?? null);
  }

  debug(message: string, payload?: object): void {
    this.instance.debug(message, payload ?? null);
  }

  info(message: string, payload?: object): void {
    this.instance.info(message, payload ?? null);
  }

  warn(message: string, payload?: object): void {
    this.instance.warn(message, payload ?? null);
  }

  error(message: string, payload?: object): void {
    this.instance.error(message, payload ?? null);
  }

  child(name: string) {
    // Return this class’ instance instead of exposing internal logger
    return new ConsoleLogger(name, this.instance);
  }

  setLogLevel(level: LogLevel) {
    switch (level) {
      case 'trace':
        this.instance.settings.minLevel = 1;
        break;
      case 'debug':
        this.instance.settings.minLevel = 2;
        break;
      case 'info':
        this.instance.settings.minLevel = 3;
        break;
      case 'warn':
        this.instance.settings.minLevel = 4;
        break;
      case 'error':
        this.instance.settings.minLevel = 5;
        break;
      case 'fatal':
        this.instance.settings.minLevel = 6;
        break;
      default:
    }
  }
}

/**
 * No-op logger, does nothing
 */
export class NullLogger implements AbstractLogger {
  trace(_message: string, _payload?: object): void {
    this.noop();
  }

  debug(_message: string, _payload?: object): void {
    this.noop();
  }

  info(_message: string, _payload?: object): void {
    this.noop();
  }

  warn(_message: string, _payload?: object): void {
    this.noop();
  }

  error(_message: string, _payload?: object): void {
    this.noop();
  }

  child(_name: string): AbstractLogger {
    // Return self as building logger hierarchy is redundant
    return this;
  }

  setLogLevel(_level: LogLevel): void {
    this.noop();
  }

  /**
   * Do nothing
   *
   * Exists mainly to avoid ESLint “empty method” errors
   */
  private noop() {
    // Do nothing
  }
}
